import { pick } from 'lodash';

import { danger } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, get, post, put } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import {
  DEFAULT_LIST_CHANNELS,
  POSTS_AMOUNT_INSTAGRAM,
  POSTS_AMOUNT_TIKTOK,
  PROPOSAL_SORT_TO_QUERY_PARAM,
  isTileIncomplete,
  mapInternalListEntry,
  mapListAPIResponseEntry
} from 'modules/proposal/utils/creator-proposal-utils';

import type {
  Action,
  PaginationLinks,
  PaginationMeta,
  ProposalImage,
  ProposalListMember,
  ProposalListMemberAPI,
  ProposalPageFilterOption,
  ProposalPageSortOption,
  ThunkAction
} from 'types';
import type { ProposalTileChannelType } from 'modules/proposal/types/ProposalTile.type';
import { Querystring } from 'utils/url';

applyTranslations(require('./proposal-reducer-errors.i18n.json'));

const SET_LIST = 'creatorProposal/SET_LIST';
const UPDATE_ENTRY = 'creatorProposal/UPDATE_ENTRY';
const SET_LIST_LOADING_STATUS = 'creatorProposal/SET_LIST_LOADING_STATUS';
const RESET_LIST = 'creatorProposal/RESET_LIST';
const LOADING_PROFILE_IDS = 'creatorProposal/LOADING_PROFILE_IDS';

type State = {
  loadingStatus: boolean,
  list: Array<ProposalListMember>,
  paginationLinks?: PaginationLinks,
  paginationMeta?: PaginationMeta,
  loadingProfileIds: { [profileId: number]: boolean }
};

const initialState: State = {
  loadingStatus: false,
  list: [],
  paginationLinks: undefined,
  paginationMeta: undefined,
  loadingProfileIds: {}
};

function setListLoadingStatus(loadingStatus: boolean) {
  return {
    type: SET_LIST_LOADING_STATUS,
    payload: {
      loadingStatus
    }
  };
}

export function setLoadingProfileIds(loadingProfileIds: { [profileId: number]: boolean }) {
  return {
    type: LOADING_PROFILE_IDS,
    payload: {
      loadingProfileIds
    }
  };
}

export function resetList(): Action {
  return {
    type: RESET_LIST
  };
}

function listLoaded(
  list: Array<ProposalListMember>,
  links: PaginationLinks,
  meta: PaginationMeta
) {
  return {
    type: SET_LIST,
    payload: {
      list,
      links,
      meta
    }
  };
}

function updateEntry(newDataObject: ProposalListMember) {
  return {
    type: UPDATE_ENTRY,
    payload: {
      newDataObject
    }
  };
}

type CreatorListResponse = {
  data: {
    data: Array<ProposalListMemberAPI>,
    links: PaginationLinks,
    meta: PaginationMeta
  }
};

export function loadCreatorList(
  organizationId: number,
  listId: number,
  page: number,
  filter: ProposalPageFilterOption,
  sort: ProposalPageSortOption
): ThunkAction {
  return async (dispatch) => {
    dispatch(setListLoadingStatus(true));
    try {
      const query = {
        page,
        filter: (filter !== 'all') ? filter : undefined,
        sort: PROPOSAL_SORT_TO_QUERY_PARAM[sort]
      };

      const url = API.END_POINTS.CREATOR_PROPOSAL
        .creatorList(organizationId, listId, Querystring.objectToString(query));
      const response = await get(url, {}, { suppressGlobalNotification: true }) as CreatorListResponse;
      const {
        data,
        links,
        meta
      } = response.data;
      dispatch(listLoaded(data.map(mapListAPIResponseEntry), links, meta));
    } catch (error) {
      dispatch(danger(__t('proposal_reducer_errors.list_fetch_failed')));
      throw error;
    } finally {
      dispatch(setListLoadingStatus(false));
    }
  };
}

export function loadAllCreatorsContent(
  organizationId: number,
  listId: number,
  page: number,
  filter: ProposalPageFilterOption,
  sort: ProposalPageSortOption): ThunkAction {
  return async (dispatch) => {
    const url = API.END_POINTS.CREATOR_PROPOSAL.loadAllCreatorsContent(organizationId, listId);

    dispatch(showLoading(url));

    try {
      await post(url, {}, {
        suppressGlobalNotification: true
      });

      return dispatch(loadCreatorList(organizationId, listId, page, filter, sort));
    } catch (error) {
      return dispatch(danger(__t('proposal_reducer_errors.bulk_content_load_failed')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function updateMemberEntry(organizationId: number, listId: number, entry: ProposalListMember): ThunkAction {
  return async (dispatch) => {
    try {
      const url = API.END_POINTS.CREATOR_PROPOSAL.creatorListMember(organizationId, listId, entry.id);
      await put(url, { data: mapInternalListEntry(entry) }, { suppressGlobalNotification: true });

      dispatch(updateEntry(entry));
    } catch (error) {
      dispatch(danger(__t('proposal_reducer_errors.list_update_failed')));
      throw error;
    }
  };
}


export function setMedia(
  organizationId: number,
  listId: number,
  creatorId: number,
  media: Array<ProposalImage | null>,
  channelType: ProposalTileChannelType
): ThunkAction {
  return (dispatch, getState) => {
    const memberDataObject: ProposalListMember | undefined = getState()
      .proposalCreatePage
      .list
      .find((entry) => entry.id === creatorId);

    const listChannels = getState().proposalCreatePageInfo?.info?.channels ?? DEFAULT_LIST_CHANNELS;

    if (!memberDataObject) {
      return Promise.reject();
    }

    // fill media if it has less than five items
    const mediaToSend: Array<ProposalImage | null | {}> = media;
    const amountOfImages = channelType === 'tiktok' ? POSTS_AMOUNT_TIKTOK : POSTS_AMOUNT_INSTAGRAM;
    for (let i = 0; i < amountOfImages; i++) {
      if (!mediaToSend[i]) {
        mediaToSend[i] = {};
      }
    }

    const mediaDataWithLoadingState = {
      ...memberDataObject,
      images: {
        ...memberDataObject.images,
        [channelType]: mediaToSend
      }
    };

    dispatch(updateEntry(mediaDataWithLoadingState));

    const newMemberDataObject = {
      ...memberDataObject,
      images: {
        ...memberDataObject.images,
        [channelType]: mediaToSend.map(postItem => pick(postItem, ['url', 'imageUrl']))
      }
    };

    const isIncomplete = isTileIncomplete(listChannels, newMemberDataObject);
    const newCompleteStatus = memberDataObject?.approvalStatus === 'incomplete'
      ? 'pending' // switch to pending if was incomplete
      : memberDataObject?.approvalStatus; // stay with current one (approved/rejected/etc.)

    return dispatch(updateMemberEntry(organizationId, listId, {
      ...newMemberDataObject,
      approvalStatus: isIncomplete ? 'incomplete' : newCompleteStatus
    }));
  };
}

export default createReducer<State, any>('proposalCreatePage', initialState, {
  [RESET_LIST]: () => initialState,
  [SET_LIST_LOADING_STATUS]: (state, { loadingStatus }) => ({
    ...state,
    loadingStatus
  }),
  [SET_LIST]: (state, { list, links, meta }) => ({
    ...state,
    list,
    paginationLinks: links,
    paginationMeta: meta
  }),
  [UPDATE_ENTRY]: (state, { newDataObject }) => ({
    ...state,
    list: state.list.map((entry) => {
      if (entry.id !== newDataObject.id) {
        return entry;
      }

      return newDataObject;
    })
  }),
  [LOADING_PROFILE_IDS]: (state, { loadingProfileIds }) => ({
    ...state,
    loadingProfileIds
  })
});
